<template>
    <section class="email-usage-section">
        <ds-card :elevation="elevation" :class="cardBorder">
            <ds-card-body>
                <template v-if="usageError" class="error-message">
                    <div class="reload-info-card-content">
                        <ds-icon name="info-circle" class="reload-icon" />

                        <span>{{ $t('myaccount.usage.email.loading.error') }}</span>
                    </div>
                </template>

                <template v-else>
                    <div class="section-title">
                        <ds-icon name="email" class="email-icon" />
                        <span>{{ $t('global.email') }}</span>
                        <div class="icon-popover">
                            <ds-popover position="right">
                                <template #reference>
                                    <ds-icon-button dense name="info-circle" class="info-icon" />
                                </template>

                                <template #default>
                                    <div class="popover-detail">
                                        <p><strong>{{ $t('global.recipient.singular') }}</strong>: {{ $t('myaccount.usage.email.recipient.pop.text') }}</p>
                                        <p><strong>{{ $t('myaccount.usage.email.sent') }}</strong>: {{ $t('myaccount.usage.email.sent.pop.text') }}</p>
                                        <p><strong>{{ $t('myaccount.usage.email.send.rate') }}</strong>: {{ $t('myaccount.usage.email.send.rate.pop.text') }}</p>
                                        <p>
                                            <a href="https://help.keap.com/help/email-usage-and-overages" target="_blank">
                                                {{ $t('myaccount.why.is.this.important') }}
                                            </a>
                                        </p>
                                    </div>
                                </template>
                            </ds-popover>
                        </div>
                    </div>

                    <div class="email-progress-section">
                        <span class="email-total-sent">{{ $t('myaccount.usage.emails.sent') }}</span>
                        <div class="gauge">
                            <span>0</span>
                            <span class="number-of-emails-limit">
                                {{ getEmailSendingLimitText }}
                            </span>
                        </div>
                        <div v-if="emailsUsedPercent<0.8">
                            <ds-progress-bar
                                class="total-email-progress-bar"
                                data-qa="total-email-progress-bar"
                                :value="emailSendingActual"
                                :max="emailSendingLimit"
                            />
                            <p class="progress-bar-description" data-qa="progress-bar-totalEmailsSent">
                                {{ getEmailsSentWithLimitText }}
                            </p>
                        </div>
                        <div v-else-if="emailsUsedPercent<1">
                            <ds-progress-bar
                                class="total-email-progress-bar-close-to-full"
                                data-qa="total-email-progress-bar"
                                :value="emailSendingActual"
                                :max="emailSendingLimit"
                            />
                            <p class="progress-bar-description" data-qa="progress-bar-totalEmailsSent">
                                {{ getEmailsSentWithLimitText }}
                                <ds-icon name="alert-triangle" class="warning-icon" />
                            </p>
                        </div>
                        <div v-else>
                            <ds-progress-bar
                                class="total-email-progress-bar-over-full"
                                data-qa="total-email-progress-bar"
                                :value="emailSendingLimit"
                                :max="emailSendingActual"
                            />
                            <p class="progress-bar-description" data-qa="progress-bar-totalEmailsSent">
                                {{ getEmailsSentWithLimitText }}
                                <ds-icon name="alert-octagon" class="alert-icon" />
                            </p>
                        </div>

                        <span class="email-send-rate">{{ $t('myaccount.usage.email.send.rate') }}</span>

                        <div class="gauge">
                            <span>{{ $t('myaccount.usage.email.base.recipient.rate') }}</span>
                            <span class="number-of-emails-limit">
                                {{ getUniqueRecipientRatioLimitText }}
                            </span>
                        </div>

                        <div v-if="rateUsedPercent<0.8">
                            <ds-progress-bar
                                class="email-send-rate-progress-bar"
                                data-qa="email-send-rate-progress-bar"
                                :value="uniqueRecipientRatioActual"
                                :max="uniqueRecipientRatioLimit"
                            />
                            <p class="progress-bar-description" data-qa="progress-bar-emailSendRate">
                                {{ getUniqueRecipientRatioActualText }}
                            </p>
                        </div>
                        <div v-else-if="rateUsedPercent<1">
                            <ds-progress-bar
                                class="email-send-rate-progress-bar-close-to-full"
                                data-qa="email-send-rate-progress-bar"
                                :value="uniqueRecipientRatioActual"
                                :max="uniqueRecipientRatioLimit"
                            />
                            <p class="progress-bar-description" data-qa="progress-bar-emailSendRate">
                                {{ getUniqueRecipientRatioActualText }}
                                <ds-icon name="alert-triangle" class="warning-icon" />
                            </p>
                        </div>
                        <div v-else>
                            <ds-progress-bar
                                class="email-send-rate-progress-bar-over-full"
                                data-qa="email-send-rate-progress-bar"
                                :value="uniqueRecipientRatioLimit"
                                :max="uniqueRecipientRatioActual"
                            />
                            <p class="progress-bar-description" data-qa="progress-bar-emailSendRate">
                                {{ getUniqueRecipientRatioActualText }}
                                <ds-icon name="alert-octagon" class="alert-icon" />
                            </p>
                        </div>
                    </div>

                    <ds-outline-button
                        v-if="rateUsedPercent > 1 && emailsUsedPercent > 1"
                        data-qa="view-charges-button"
                        class="view-charges-button"
                        @click="openViewChargesModal"
                    >
                        {{ $t('myaccount.view.charges') }}
                    </ds-outline-button>

                    <email-charges-modal
                        :is-open="isViewChargesModalOpen"
                        :total-cost="totalCost"
                        :currency-code="currencyCode"
                        :currency-symbol="currencySymbol"
                        :email-sent-limit="emailSendingLimit"
                        :email-sent-actual="emailSendingActual"
                        :unique-recipient-ratio-limit="uniqueRecipientRatioLimit"
                        :unique-recipient-ratio-actual="uniqueRecipientRatioActual"
                        :email-unit-price="emailUnitPrice"
                        :unique-addresses-sent-to="uniqueAddressesSentTo"
                        @close="closeViewChargesModal"
                    />
                </template>
            </ds-card-body>
        </ds-card>
    </section>
</template>
<script>
import axios from 'axios';
import EmailChargesModal from './EmailChargesModal';

export default {
    name: 'AccountEmailVolumeMonthlyUsage',

    components: { EmailChargesModal },

    data() {
        return {
            emailSendingLimit: 250000,
            emailSendingActual: 0,
            uniqueRecipientRatioLimit: 10,
            uniqueRecipientRatioActual: 0,
            uniqueAddressesSentTo: 0,
            emailUnitPrice: '0',
            totalCost: '0',
            currencyCode: 'USD',
            currencySymbol: '$',
            loading: false,
            fetchError: false,
            isViewChargesModalOpen: false,
        };
    },

    created() {
        this.load();
    },

    computed: {
        usageError() {
            return this.fetchError;
        },

        elevation() {
            return this.emailsUsedPercent > 1 && this.rateUsedPercent > 1 ? 'z4' : 'z0';
        },

        cardBorder() {
            if (this.elevation === 'z0') {
                return 'border-none';
            }

            if (this.elevation === 'z4') {
                return 'elevated';
            }

            return '';
        },

        emailsUsedPercent() {
            return this.emailSendingActual / this.emailSendingLimit;
        },

        rateUsedPercent() {
            return this.uniqueRecipientRatioActual / this.uniqueRecipientRatioLimit;
        },

        getEmailSendingLimitText() {
            const limitWithCommas = this.numberWithCommas(this.emailSendingLimit);

            return `${limitWithCommas} (${this.$t('myaccount.limit.lowercase')})`;
        },

        getUniqueRecipientRatioLimitText() {
            return `${this.uniqueRecipientRatioLimit} (${this.$t('myaccount.limit.lowercase')})`;
        },

        getEmailsSentWithLimitText() {
            const sendingWithCommas = this.numberWithCommas(this.emailSendingActual);
            const limitWithCommas = this.numberWithCommas(this.emailSendingLimit);

            return this.$t('myaccount.usage.emails.sent.with.limit', {
                sent: sendingWithCommas,
                limit: limitWithCommas,
            });
        },

        getUniqueRecipientRatioActualText() {
            return `${this.uniqueRecipientRatioActual}x`;
        },
    },

    methods: {
        async load() {
            try {
                this.loading = true;
                this.fetchError = false;
                const { data: { emailVolumeMetrics } } = await axios.get('/app/myAccount/emailVolumeUsage');

                this.emailSendingLimit = emailVolumeMetrics.emailSendingLimit;
                this.emailSendingActual = emailVolumeMetrics.emailSendingActual;
                this.uniqueRecipientRatioLimit = emailVolumeMetrics.uniqueRecipientRatioLimit;
                this.uniqueRecipientRatioActual = emailVolumeMetrics.uniqueRecipientRatioActual;
                this.uniqueAddressesSentTo = emailVolumeMetrics.uniqueAddressesSentTo;
                this.emailUnitPrice = emailVolumeMetrics.emailUnitPrice;
                this.totalCost = emailVolumeMetrics.totalCost;
                this.currencyCode = emailVolumeMetrics.currencyCode;
                this.currencySymbol = emailVolumeMetrics.currencySymbol;

                this.loading = false;
            } catch (error) {
                this.loading = false;
                this.fetchError = true;
            }
        },

        openViewChargesModal() {
            this.isViewChargesModalOpen = true;
        },

        closeViewChargesModal() {
            this.isViewChargesModalOpen = false;
        },

        numberWithCommas(x) {
            return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
    },
};
</script>

<style lang="scss" scoped>
.email-usage-section {
    width: px-to-rem(400);
}

.section-title {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    font-size: $font-size-md;
    font-weight: 500;
    line-height: $line-height-xl;
    margin-bottom: $spacing-100;

    span {
        padding-left: $spacing-050;
    }
}

/* This derived from a computed property, cardBorder */
.border-none {
    box-shadow: none;
    /* To make up for the padding on the elements when there is no border */
    margin-left: px-to-rem(-8);
}

.icon-popover {
    /* Want it this item to stick to the far right */
    margin-left: auto;
    display: inherit;

    --popover-width: #{px-to-rem(420)};
}

.popover-detail {
    font-weight: 400;
    padding: $spacing-200 $spacing-200 0;
}

.email-total-sent, .email-send-rate {
    font-size: $font-size-md;
    @include margin($spacing-200 0 0 0);
}

.total-email-progress-bar, .email-send-rate-progress-bar {
    margin-bottom: 0;
}

.total-email-progress-bar-close-to-full, .email-send-rate-progress-bar-close-to-full {
    margin-bottom: 0;
    --progress-bar-color: #{$color-yellow-700};

    &::v-deep .bar-container {
        background-color: $color-yellow-100;
    }
}

.total-email-progress-bar-over-full, .email-send-rate-progress-bar-over-full {
    margin-bottom: 0;
    --progress-bar-color: #{$color-red-400};

    &::v-deep .bar-container {
        background-color: $color-red-800;
    }
}

.info-icon, .number-of-emails-limit {
    float: right;
}

.email-icon {
    vertical-align: middle;
}

.gauge {
    font-size: $font-size-xs;
}

.progress-bar-description {
    font-size: $font-size-sm;
}

.warning-icon {
    --icon-color: #{$color-yellow-700};
    --icon-size: #{$icon-size-small};
    vertical-align: middle;
}

.alert-icon {
    --icon-color: #{$color-red-800};
    --icon-size: #{$icon-size-small};
    vertical-align: middle;
}
</style>
